import { Rule, Requirement } from "@/shared/domain/layouts/LayoutRecord";
import { MessagePathNode, pathParser, TopicNode } from "@/shared/domain/topics";
import { pathMatcher } from "@/shared/domain/topics/PathMatcher";
import { TopicData } from "@/shared/state/visualization";

export enum MatchFailure {
  NO_PATH_MATCH = "NO_PATH_MATCH",
  NO_DATATYPE_MATCH = "NO_DATATYPE_MATCH",
  NO_COMPLETE_MATCH = "NO_COMPLETE_MATCH",
  NO_REQUIREMENTS_SPECIFIED = "NO_REQUIREMENTS_SPECIFIED",
}

interface RuleMatch {
  rule: Rule;
  topicData: TopicData[];
}

interface FailedRequirement {
  rule: Rule;
  requirement: Requirement;
  reason: MatchFailureReason;
}

type MatchFailureReason = {
  type: MatchFailure;
  message: string;
};

interface RuleMatchResult {
  matches: RuleMatch[];
  failures: FailedRequirement[];
}

class LayoutRuleMatcher {
  public constructor() {}

  /**
   * Matches a rule against a set of candidates to find and return the first match.
   *
   * @param rule - Rule to match against
   * @param candidates - Available topics to match against
   */
  findMatches(rule: Rule, candidates: TopicNode[]): RuleMatchResult {
    const matches: RuleMatch[] = [];
    const failures: FailedRequirement[] = [];

    if (rule.requirements.length === 0) {
      failures.push({
        rule,
        requirement: { topicMessagePath: "" },
        reason: {
          type: MatchFailure.NO_REQUIREMENTS_SPECIFIED,
          message: "No requirements specified for rule",
        },
      });
      return { matches, failures };
    }

    // Go through each requirement and find matches
    const matchesByRequirement = rule.requirements.map((requirement) => {
      const pathMatches = this.#evaluateByPath(requirement, candidates);

      if (pathMatches.length === 0) {
        failures.push({
          rule,
          requirement,
          reason: {
            type: MatchFailure.NO_PATH_MATCH,
            message: `No matching path found for ${requirement.topicMessagePath}`,
          },
        });
        return { requirement, matches: [] };
      }

      const typeMatches = this.#evaluateByDataType(requirement, pathMatches);
      if (typeMatches.length === 0) {
        failures.push({
          rule,
          requirement: requirement,
          reason: {
            type: MatchFailure.NO_DATATYPE_MATCH,
            message: `No matching datatype found for ${requirement.dataType}`,
          },
        });
        return { requirement, matches: [] };
      }

      return {
        requirement,
        matches: typeMatches.map((match) => match.toTopicData()),
      };
    });

    // If any requirement has no matches, we can't form complete matches
    if (
      matchesByRequirement.length === 0 ||
      matchesByRequirement.some((m) => m.matches.length === 0)
    ) {
      return { matches, failures };
    }

    /**
     * Form complete matches from the matches for each requirement
     *
     * This logic is used to create a "Match Set" which as of April 22, 2024 is only used for the Map Panel
     * which needs both lat and lon data.
     *
     * The logic is we find (n) matches for requirement 1 and (m) matches for requirement 2
     * and then we form min(n, m) complete match sets.
     *
     * We then record any leftover matches as a requirement failure.
     */
    const minMatches = Math.min(
      ...matchesByRequirement.map((m) => m.matches.length),
    );
    for (let i = 0; i < minMatches; i++) {
      matches.push({
        rule,
        topicData: matchesByRequirement.map((m) => m.matches[i]),
      });
    }

    // Record any remaining matches as a failure
    matchesByRequirement.forEach(({ requirement, matches }) => {
      if (matches.length > minMatches) {
        failures.push({
          rule,
          requirement,
          reason: {
            type: MatchFailure.NO_COMPLETE_MATCH,
            message: `Found ${matches.length} matches but could only use ${minMatches} due to other requirements`,
          },
        });
      }
    });

    return { matches, failures };
  }

  #evaluateByDataType(
    requirement: Requirement,
    candidates: MessagePathNode[],
  ): MessagePathNode[] {
    return candidates.filter(
      (candidate) =>
        candidate.data.canonical_data_type === requirement.dataType,
    );
  }

  #evaluateByPath(
    requirement: Requirement,
    candidates: TopicNode[],
  ): MessagePathNode[] {
    const parsedPath = pathParser.parse(requirement.topicMessagePath);
    return pathMatcher
      .findMatches(parsedPath, candidates)
      .filter((match) => match.isComplete)
      .map((match) => match.match);
  }
}

export const layoutRuleMatcher = new LayoutRuleMatcher();
